import React, {useContext, useEffect, useRef, useState} from 'react';
import {useHistory, useParams} from "react-router-dom";
import {FormattedMessage, injectIntl} from "react-intl";

// Material UI
import {Grid} from "@mui/material";

// Components
import RightDrawer from "components/WMS/Mobile/RightDrawer";
import ToggleNavigation from "components/WMS/Mobile/Toggle/ToggleNavigation";
import Toggle from "components/WMS/Mobile/Toggle/Toggle";
import ClickableChip from "components/WMS/ClickableChip";
import AddRetailers from "./components/AddRetailers";
import CustomButton from "components/CustomButton";
import BottomActionContainer from "components/WMS/Mobile/BottomActionContainer";

// Local components
import RetailerItemsDrawer from "./components/RetailerItemsDrawer";
import NoValidateProducts from "./components/NoValidateProducts";
import RetailerList from "./components/RetailerList";
import RefreshPopup from "./components/Popups/RefreshPopup";
import InTrolleyToast from "./components/Toasts/InTrolleyToast";
import EndPickingRetailerToast from "./components/Toasts/EndPickingRetailerToast";
import TaskHeaderRightContent from "./components/TaskHeaderRightContent";
import EndPickingMissionPopup from "./components/Popups/EndPickingMissionPopup";
import RefreshButton from "./components/RefreshButton";
import ZoneContainer from "./components/ZoneContainer";
import ProductNotFoundToast from "./components/Toasts/ProductNotFoundToast";
import ProductAlreadyInTrolleyToast from "./components/Toasts/ProductAlreadyInTrolleyToast";

// Context
import {BarCodeScannerContext} from "contexts/barCodeScannerContext";
import {MissionContext} from "./context/MissionContext";
import {WMSContext} from "../context/WMSContext";

// API
import {batchWmsItemInTrolley, getWmsItems, wmsItemInTrolley} from "api/wms/wmsItems/wmsItems";
import pusherClient from "utils/pusherClient";
import {getTask} from "api/wms/tasks/list";
import {getWMSContainerWithCodeBar} from "api/wms/wmsContainers/wmsContainers";
import {endMission} from "api/wms/tasks/end";
import {addRetailerItemsToTask} from "api/wms/tasks/addRetailerItemsToTask";

// Utils
import {WmsItemsStatus} from "utils/WmsItemsStatus";
import {WmsTaskStatus} from "utils/WMSTaskStatus";
import {WMSRoutes} from "utils/WMSRoutes";
import BeatLoaderRegular from "../../../components/loaders/BeatLoaderRegular";
import AddProductFromRetailerNotFound from "./components/Toasts/AddProductFromRetailerNotFound";

function Task(props) {
    const [openRetailerItemList, setOpenRetailerItemList] = useState(false);
    const [openAddRetailers, setOpenAddRetailers] = useState(false);

    const [openScannedItemToast, setOpenScannedItemToast] = useState(false);
    const [scannedItemMulti, setScannedItemMulti] = useState(false)
    const [openEndPickingRetailerToast, setOpenEndPickingRetailerToast] = useState({
        isOpen: false,
        lastScannedWmsItem: null
    });
    const [openProductNotFoundToast, setOpenProductNotFoundToast] = useState(false);
    const [openProductAlreadyInTrolleyToast, setOpenProductAlreadyInTrolleyToast] = useState(false);
    const [openNoOtherProductFoundToast, setOpenNoOtherProductFoundToast] = useState(false)

    const [openRefreshPopup, setOpenRefreshPopup] = useState(false);
    const [openEndPickingMissionPopup, setOpenEndPickingMissionPopup] = useState(false);
    const [openCancelMissionPopup, setOpenCancelMissionPopup] = useState(false);

    const [retailers, setRetailers] = useState([]);

    const [itemsToPick, setItemsToPick] = useState(0)
    const [itemsInTrolley, setItemsInTrolley] = useState(0)
    const [itemsCancelled, setItemsCancelled] = useState(0);

    const [retailerItems, setRetailerItems] = useState([]);
    const [retailerNewWmsItems, setRetailerNewWmsItems] = useState(null);
    const [selectedRetailers, setSelectedRetailers] = useState({});
    const [retailersToBeAddedToMission, setRetailersToBeAddedToMission] = useState([]);

    const [zoneFilters, setZoneFilters] = useState({});

    const [selectedZone, setSelectedZone] = useState('all');
    const [filterValue, setFilterValue] = React.useState(0);

    const [selectedRetailer, setSelectedRetailer] = useState(null);

    const [wmsItems, setWmsItems] = useState(null);
    const [availableWmsItems, setAvailableWmsItems] = useState([]);

    const [currentContainer, setCurrentContainer] = useState(null);

    const [loading, setLoading] = useState(true)
    const isMissionEnding = useRef(false)
    const pusherFallbackTimers = useRef(new Map())
    const pendingItemIds = useRef(new Set())

    const {scannedString, resetScannedString, setEnableScan, setEnableChainedScan} = useContext(BarCodeScannerContext);
    const {currentInventory, currentMember, setCurrentTask, currentTask, currentOrganization} = useContext(WMSContext);

    let { taskId, containerBarcode } = useParams();

    const history = useHistory();

    // unmount cleanup
    useEffect(() => {
        return () => {
            setEnableChainedScan(false);
            resetScannedString();
        };
    }, []);

    // did mount
    useEffect(() => {
        if (!currentInventory) {
            return;
        }

        getWMSContainerWithCodeBar(containerBarcode).then((container) => {
            setCurrentContainer(container);
        })

        getTask(taskId).then((task) => {
            if (task.status !== WmsTaskStatus.IN_PROGRESS) {
                history.replace(WMSRoutes.PICKING)
            }
            setCurrentTask(task);
        })

        refreshMission()
    }, [currentInventory]);

    // on scan
    useEffect(() => {
        if (!scannedString) {
            return;
        }

        setEnableScan(false)

        const matchedWmsItems = wmsItems.filter(item => item.barcode === scannedString);

        if (matchedWmsItems.length === 0) {
            setOpenProductNotFoundToast(true);
            setEnableScan(true);
            return;
        }

        const allAlreadyInTrolley = matchedWmsItems.every(item => WmsItemsStatus.IN_TROLLEY === item.status);

        if (allAlreadyInTrolley) {
            resetScannedString()
            setEnableScan(true);
            setOpenProductAlreadyInTrolleyToast(true);
            return;
        }

        const item = matchedWmsItems.find(item => item.status === WmsItemsStatus.PICKING);

        if (!item) {
            setEnableScan(true)
            return;
        }

        if (pendingItemIds.current.has(item.id)) {
            setEnableScan(true)
            return;
        }

        putWmsItemInTrolley(item).then(() => {
            if (wasLastItemOfRetailer(item)) {
                setOpenEndPickingRetailerToast({
                    isOpen: true,
                    lastScannedWmsItem: item
                })
            } else {
                setOpenScannedItemToast(true);
                setScannedItemMulti(item.lineItem.currentShipment.multi)
            }
        }).catch(() => {
            setEnableScan(true)
        })
    }, [scannedString]);

    // on wmsItems list refresh
    useEffect(() => {
        if (!Array.isArray(wmsItems) || !currentTask) {
            return;
        }

        setItemsCancelled(getCancelledTotal(wmsItems));

        const availableWmsItems = wmsItems;

        setAvailableWmsItems(availableWmsItems);
        setRetailers(getRetailersByWmsItem(availableWmsItems));

        setItemsToPick(getPickingTotal(availableWmsItems));
        setItemsInTrolley(getInTrolleyTotal(availableWmsItems));

        setZoneFilters(getZoneFilters(availableWmsItems));

        // when task is finished, we end the mission
        if (
            (
                wmsItems.length === 0
                || wmsItems.every((item) => item.status === WmsItemsStatus.IN_TROLLEY)
            )
            && currentTask.status === WmsTaskStatus.IN_PROGRESS
            && currentMember
            && !isMissionEnding.current
        ) {
            isMissionEnding.current = true;
            setFilterValue(1)
        }
        setEnableChainedScan(true)
        setEnableScan(true)
        setLoading(false)
    }, [wmsItems, currentTask]);

    // when changing zone
    useEffect(() => {
        if (!availableWmsItems || availableWmsItems.length === 0) {
            return;
        }

        let items = []

        if ('no-place' === selectedZone) {
            items = availableWmsItems.filter((item) => 0 === item.places.length);
        } else {
            items = availableWmsItems.filter((item) => item.places.some(place => place['@id'] === selectedZone) || selectedZone === 'all');
        }

        setRetailers(getRetailersByWmsItem(items));
    }, [selectedZone]);

    // when
    useEffect(() => {
        if (retailersToBeAddedToMission.length === 0) {
            return;
        }

        if (openRefreshPopup) {
            refreshMission()
        }

        setOpenRefreshPopup(true);
    }, [retailersToBeAddedToMission])

    // Pusher real-time subscription for wms item status changes
    useEffect(() => {
        if (!currentInventory || !currentOrganization) {
            return;
        }

        const channelName = `wms_item_${currentInventory.id}${currentOrganization.id}`;
        const channel = pusherClient.subscribe(channelName);

        channel.bind('status_changed', (data) => {
            const itemId = data.updatedWmsItem.id;
            clearTimeout(pusherFallbackTimers.current.get(itemId));
            pusherFallbackTimers.current.delete(itemId);
            pendingItemIds.current.delete(itemId);
            setWmsItems(prev => prev.map(item =>
                item.id === itemId
                    ? {...item, status: data.updatedWmsItem.status}
                    : item
            ));
        });

        return () => {
            pusherFallbackTimers.current.forEach(timer => clearTimeout(timer));
            pusherFallbackTimers.current.clear();
            channel.unbind('status_changed');
            pusherClient.unsubscribe('private-' + channelName);
        };
    }, [currentInventory, currentOrganization]);

    function applyWmsItemUpdate(wmsItemId, updatedWmsItem) {
        if (!updatedWmsItem || !updatedWmsItem.status) {
            return;
        }

        setWmsItems(prev => Array.isArray(prev)
            ? prev.map(item => item.id === wmsItemId
                ? {
                    ...item,
                    status: updatedWmsItem.status,
                    wmsContainer: updatedWmsItem.wmsContainer ?? item.wmsContainer,
                }
                : item
            )
            : prev
        );
    }

    async function putWmsItemInTrolley(wmsItem) {
        if (!wmsItem) {
            return;
        }

        pendingItemIds.current.add(wmsItem.id);

        // Computed before the update: does this item complete the mission?
        const completesMission = Array.isArray(wmsItems)
            && wmsItems.every(item => item.id === wmsItem.id || item.status === WmsItemsStatus.IN_TROLLEY);

        let updatedWmsItem;
        try {
            updatedWmsItem = await wmsItemInTrolley(wmsItem, currentContainer.codebar);
        } catch (error) {
            pendingItemIds.current.delete(wmsItem.id);
            throw error;
        }

        // A 2xx on the PATCH means the API moved the item to in_trolley in this container: update
        // it in place instead of reloading the whole mission (`GET /w_m_s_items?...&pagination=false`),
        // whose cost grows with the mission size and which keeps the scanner disabled until it returns.
        // The endpoint's body is not a serialized item, so only read it defensively.
        applyWmsItemUpdate(wmsItem.id, {
            status: WmsItemsStatus.IN_TROLLEY,
            wmsContainer: currentContainer,
            ...(updatedWmsItem && typeof updatedWmsItem === 'object' && updatedWmsItem.status ? updatedWmsItem : {}),
        });

        // On the last item, resynchronize the whole mission once before the end-of-mission flow,
        // in case items were added or cancelled by someone else meanwhile.
        if (completesMission) {
            refreshMission();
        }

        // Pusher may confirm the status change before the PATCH promise resolves: the API publishes
        // on kernel.terminate, right after sending the response. The handler then already removed
        // the item from pendingItemIds and there is nothing left to wait for.
        if (!pendingItemIds.current.has(wmsItem.id)) {
            return;
        }

        clearTimeout(pusherFallbackTimers.current.get(wmsItem.id));
        pusherFallbackTimers.current.set(wmsItem.id, setTimeout(() => {
            pendingItemIds.current.delete(wmsItem.id);
            pusherFallbackTimers.current.delete(wmsItem.id);
            refreshMission();
        }, 5000));
    }

    async function putBatchItemsInTrolley(itemsSku) {
        if (!itemsSku || !Array.isArray(itemsSku)) {
            return;
        }

        const res = await batchWmsItemInTrolley(itemsSku, currentInventory.id, taskId, containerBarcode)

        if (res) {
            resetScannedString()
        }
    }

    function wasLastItemOfRetailer(wmsItem) {
        const retailerId = wmsItem.lineItem.retailer.id
        const pickingItems = availableWmsItems.filter(
            item => item.lineItem.retailer.id === retailerId && item.status === WmsItemsStatus.PICKING
        );

        return pickingItems.length === 1 && pickingItems[0] === wmsItem;
    }

    async function refreshMission() {
        if (retailersToBeAddedToMission.length > 0) {
            await addRetailerItemsToTask(taskId, currentInventory.id, retailersToBeAddedToMission).then((response) => {
                if (response.error) {
                    setOpenNoOtherProductFoundToast(true)
                }

            });

            setRetailersToBeAddedToMission([]);
            setSelectedRetailer([]);
            setRetailerNewWmsItems(null); // refresh the list when opening the add retailer drawer again
        }

        getWmsItems(`inventoryId=${currentInventory.id}&wmsTask.id=${taskId}&pagination=false`).then((response) => {
            setOpenRefreshPopup(false);
            setLoading(false)
            setWmsItems(response)
        });
    }

    function getRetailersByWmsItem(wmsItems) {
        if (!wmsItems || wmsItems.length === 0) {
            return [];
        }

        let retailersByWmsItems = []

        wmsItems.forEach((item) => {
            if (!retailersByWmsItems.some(retailer => retailer.id === item.lineItem.retailer.id)) {
                retailersByWmsItems.push(item.lineItem.retailer)
            }
        })

        return retailersByWmsItems;
    }

    function openRetailerProducts(retailer) {
        setSelectedRetailer(retailer);
        setOpenRetailerItemList(true);
    }

    function handleCloseEndPickingMissionPopup() {
        setOpenEndPickingMissionPopup(false);
    }

    function finishMissionAction() {
        if (!currentTask || !currentMember) {
            return;
        }

        if (itemsToPick >= 1) {
            setOpenCancelMissionPopup(true)
        }

        if (itemsToPick === 0) {
            setOpenEndPickingMissionPopup(true)
        }
    }

    function leaveMission() {
        if (currentTask?.status === WmsTaskStatus.IN_PROGRESS) {
            setOpenCancelMissionPopup(true);

            return;
        }

        history.replace(WMSRoutes.PICKING);
    }

    function finishMission(route) {
        endMission(currentTask, currentMember).then(() => {
            setCurrentTask({ ...currentTask, status: WmsTaskStatus.COMPLETED });
            setOpenEndPickingMissionPopup(true);
            history.replace(route);
        }).catch(() => {
            isMissionEnding.current = false;
        });
    }

    function getPickingTotal(items) {
        return items.reduce((acc, item) => acc + ((item.status === WmsItemsStatus.PICKING) ? 1 : 0), 0);
    }

    function getInTrolleyTotal(items) {
        return items.reduce((acc, item) => acc + ((item.status === WmsItemsStatus.IN_TROLLEY) ? 1 : 0), 0);
    }

    function getCancelledTotal(items) {
        return items.reduce((acc, item) => acc + ((item.status === WmsItemsStatus.CANCELED) ? 1 : 0), 0);
    }

    function getInTrolleyItems(items) {
        return items.filter(item => item.status === WmsItemsStatus.IN_TROLLEY);
    }

    function getPickingItems(items) {
        return items.filter(item => item.status === WmsItemsStatus.PICKING);
    }

    function getWmsItemsByRetailer(retailer) {
        return wmsItems.filter(item => (item.lineItem.retailer.id === retailer.id) || (retailer.id === 'noRetailer' && !item.lineItem.retailer));
    }

    function getZoneFilters(wmsItems) {
        if (!wmsItems?.length) {
            return {};
        }

        const filterZones = {
            [WmsItemsStatus.PICKING]: { 'all': { name: props.intl.formatMessage({id: 'wms.myPickingMissions.filter.all'}), count: 0 } },
            [WmsItemsStatus.IN_TROLLEY]: { 'all': { name: props.intl.formatMessage({id: 'wms.myPickingMissions.filter.all'}), count: 0 } }
        };

        const updateZones = (items, status) => {
            items.forEach(item => {
                if (0 === item.places.length) {
                    let zone = filterZones[status]['no-place'];

                    if (!zone) {
                        zone = {name: 'wms.myPickingMission.toggle.noZone', count: 0};

                        filterZones[status]['no-place'] = zone;
                    }

                    zone.count += 1;
                }
                item.places.forEach(place => {
                    let zone = filterZones[status][place['@id']];

                    if (!zone) {
                        zone = {name: place.name, count: 0};
                        filterZones[status][place['@id']] = zone;
                    }

                    zone.count += 1;
                });
                filterZones[status]['all'].count += 1;
            });
        };

        updateZones(getPickingItems(wmsItems), WmsItemsStatus.PICKING);
        updateZones(getInTrolleyItems(wmsItems), WmsItemsStatus.IN_TROLLEY);

        return filterZones;
    }

    return (
        <React.Fragment>
            <MissionContext.Provider value={{
                refreshMission,
                getPickingTotal,
                getInTrolleyTotal,
                getCancelledTotal,
                currentContainer,
                setOpenCancelMissionPopup,
                retailerNewWmsItems,
                setRetailerNewWmsItems,
                selectedRetailers,
                setSelectedRetailers,
                setRetailersToBeAddedToMission,
                retailersToBeAddedToMission,
                retailers,
                getWmsItemsByRetailer,
                loading,
                setLoading,
                putBatchItemsInTrolley,
                putWmsItemInTrolley
            }}>
                <RightDrawer
                    open={true}
                    handleClose={leaveMission}
                    headerCenterContent={(<b>{currentTask?.name || <FormattedMessage id={'wms.myPickingMissions.mission.title'} />}</b>)}
                    headerRightContent={(
                        <TaskHeaderRightContent
                            setOpenCancelMissionPopup={setOpenCancelMissionPopup}
                            openCancellationPopup={openCancelMissionPopup}
                            itemsToPick={itemsToPick}
                        />
                    )}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                        }}
                    >
                        <div
                            style={{
                                background: '#fff',
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden'
                            }}
                        >
                            <div
                                style={{
                                    padding: 16,
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <ToggleNavigation
                                    value={filterValue}
                                    onChange={(index) => setFilterValue(index)}
                                >
                                    <Toggle count={itemsToPick}>
                                        <FormattedMessage id={'wms.myPickingMissions.navigation.retrieve'} />
                                    </Toggle>
                                    <Toggle count={itemsInTrolley + itemsCancelled}>
                                        <FormattedMessage id={'wms.myPickingMissions.navigation.validated'} />
                                    </Toggle>
                                </ToggleNavigation>
                                <RefreshButton onClick={() => setOpenRefreshPopup(true)} />
                            </div>
                            <ZoneContainer>
                                {Object.keys(zoneFilters).length > 0 && Object.entries(zoneFilters[filterValue === 0 ? WmsItemsStatus.PICKING : WmsItemsStatus.IN_TROLLEY]).map(([placeId, place]) => (
                                    <ClickableChip
                                        key={place.name}
                                        count={place.count}
                                        checked={selectedZone === placeId}
                                        thick={true}
                                        noWrapText={true}
                                        onClick={() => {
                                            setSelectedZone(placeId);
                                            if (placeId === 'all') {
                                                setRetailerItems(wmsItems);
                                            } else {
                                                if ('no-place' === placeId) {
                                                    setRetailerItems(wmsItems.filter(item => 0 === item.places.length));
                                                } else {
                                                    setRetailerItems(wmsItems.filter(item => item.places.some(p => p['@id'] === placeId)));
                                                }
                                            }
                                        }}
                                    >
                                        {placeId === 'no-place' ? <FormattedMessage id={place.name}/> : place.name}
                                    </ClickableChip>
                                ))}
                            </ZoneContainer>

                            {itemsInTrolley === 0 && itemsCancelled === 0 && filterValue === 1 && (
                                <NoValidateProducts />
                            )}

                            {loading && 0 === retailers.length && (
                                <Grid
                                    item
                                    sx={{maxWidth: 'fit-content', margin: 'auto', paddingY: '16px'}}
                                >
                                    <BeatLoaderRegular loading size={15}/>
                                </Grid>
                            )}

                            <RetailerList
                                onClickRetailerLine={(retailer) => openRetailerProducts(retailer)}
                                retailers={retailers}
                                filterValue={filterValue}
                            />
                        </div>
                        <BottomActionContainer>
                            <Grid item xs>
                                <CustomButton
                                    fullWidth={true}
                                    thicker={true}
                                    disabled={itemsToPick >= 1}
                                    onClick={() => finishMissionAction()}
                                >
                                    <FormattedMessage id='wms.myPickingMissions.mission.finish' />
                                </CustomButton>
                            </Grid>
                            <Grid item>
                                <CustomButton
                                    onClick={() => setOpenAddRetailers(true)}
                                    thicker={true}
                                    color={'default'}
                                    sx={{
                                        width: '28px',
                                        padding: 0,
                                        minWidth: '48px'
                                    }}
                                >
                                    +
                                </CustomButton>
                            </Grid>
                        </BottomActionContainer>
                    </div>
                    {selectedRetailer !== null && (
                        <RetailerItemsDrawer
                            selectedFilter={filterValue}
                            open={openRetailerItemList}
                            handleClose={() => {
                                setSelectedRetailer(null)
                                setOpenRetailerItemList(false)
                            }}
                            selectedRetailer={selectedRetailer}
                            wmsItems={wmsItems}
                        />
                    )}
                    <AddRetailers
                        open={openAddRetailers}
                        handleClose={() => setOpenAddRetailers(false)}
                    />
                    <ProductNotFoundToast
                        open={openProductNotFoundToast}
                        handleClose={() => setOpenProductNotFoundToast(false)}
                    />
                    <AddProductFromRetailerNotFound
                        open={openNoOtherProductFoundToast}
                        handleClose={() => setOpenNoOtherProductFoundToast(false)}
                    />
                    <ProductAlreadyInTrolleyToast
                        open={openProductAlreadyInTrolleyToast}
                        handleClose={() => setOpenProductAlreadyInTrolleyToast(false)}
                    />
                </RightDrawer>
                <RefreshPopup
                    open={openRefreshPopup}
                    handleClose={() => setOpenRefreshPopup(false)}
                    onClick={refreshMission}
                />
                <EndPickingMissionPopup open={openEndPickingMissionPopup} handleClose={handleCloseEndPickingMissionPopup} handleValidFinishMission={finishMission} />
                <InTrolleyToast
                    isOpen={openScannedItemToast}
                    isMulti={scannedItemMulti}
                    handleClose={() => setOpenScannedItemToast(false)}
                />
                <EndPickingRetailerToast
                    wmsItem={openEndPickingRetailerToast.lastScannedWmsItem}
                    isOpen={openEndPickingRetailerToast.isOpen}
                    handleClose={() => setOpenEndPickingRetailerToast({
                        isOpen: false,
                        lastScannedWmsItem: null
                    })}
                />
            </MissionContext.Provider>
        </React.Fragment>
    );
}

export default injectIntl(Task);
